import React, { useCallback, useEffect, useState } from 'react';

import DateFnsUtils from '@date-io/date-fns';

import { makeStyles } from '@material-ui/core/styles';
import Avatar         from '@material-ui/core/Avatar';
import Button         from '@material-ui/core/Button';
import ButtonGroup    from '@material-ui/core/ButtonGroup';
import Dialog         from '@material-ui/core/Dialog';
import DialogActions  from '@material-ui/core/DialogActions';
import DialogContent  from '@material-ui/core/DialogContent';
import Table          from '@material-ui/core/Table';
import TableBody      from '@material-ui/core/TableBody';
import TableCell      from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow       from '@material-ui/core/TableRow';
import TextField      from '@material-ui/core/TextField';
import Typography     from '@material-ui/core/Typography';

import {
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';

import { create, date }  from '../util';
import SelectingAttender from '../SelectingAttender';

const days = [
  {value: 1, label: '월'},
  {value: 2, label: '화'},
  {value: 3, label: '수'},
  {value: 4, label: '목'},
  {value: 5, label: '금'},
  {value: 6, label: '토'},
  {value: 0, label: '일'},
];

const seconds = {
  fromDate: (date) => {
    return date.getHours() * 3600 +
      date.getMinutes() * 60 +
      date.getSeconds();
  },
  toDate: (seconds) => {
    const HH = Math.floor(seconds / 3600);
    const mm = Math.floor(seconds % 3600 / 60);
    return new Date(0, 0, 0, HH, mm, 0);
  },
};

const style = {
  newSchedule: makeStyles((theme) => ({
    attender: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    avatar: {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
    dialogContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(4),
    },
    dialogTitle: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
    },
    seconds: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(2),
    },
  })),
};

export default function NewSchedule(props) {
  const classes = style.newSchedule();

  const [ revision, setRevision ] = useState(0);
  const [ selectedAttender, setSelectedAttender ] = useState(create.attender());
  const {
    newSchedule,
    request,
    setNewSchedule,
    setSnackbarMessage,
  } = props.context.getStates();

  const deleteAttender = useCallback((attenderRowid) => {
    request.postSecurely('/schedule/attender/delete', {
      attender: attenderRowid,
      schedule: newSchedule.rowid,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        setRevision(revision + 1);
      }
    });
  }, [
    request,
    revision,
    newSchedule.rowid
  ]);

  const insertAttender = useCallback((attenderRowid) => {
    request.postSecurely('/schedule/attender/insert', {
      attender: attenderRowid,
      schedule: newSchedule.rowid,
    }, (response) => {
      if ('error' in response) {
        setSnackbarMessage(response.error);
      } else {
        setRevision(revision + 1);
      }
    });
  }, [
    newSchedule.rowid,
    request,
    revision,
    setSnackbarMessage
  ]);

  const selectAttenders = useCallback(() => {
    request.postSecurely('/schedule/attendings', {
      schedule: newSchedule.rowid
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else if ('attendings' in response) {
        let newAttendings = [];
        for (const attending of response.attendings) {
          if (attending['attender.rowid']) {
            newAttendings.push(create.attending(
              attending['attender.rowid'],
              attending['attender.name'],
              attending['attender.picture64'],
              attending['MAX(issued.used_at)'],
            ));
          }
        }
        newAttendings.sort(
          (a1, a2) => (a1['attenderName'] || '').localeCompare(a2['attenderName'] || '')
        );

        setNewSchedule((oldSchedule) => ({
          ...oldSchedule, attendings: newAttendings
        }));
      }
    });
  }, [
    newSchedule.rowid,
    request,
    setNewSchedule,
  ]);

  useEffect(() => {
    if (newSchedule.rowid > -2) {
      selectAttenders();
    }
  }, [
    newSchedule.rowid,
    revision,
    selectAttenders,
  ]);

  return (
    <Dialog fullWidth open={newSchedule.rowid > -2}>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogTitle}>
          <Typography variant="h6">
            {newSchedule.rowid === -1 ? '새 일정 등록' : '일정 수정'}
          </Typography>

          {newSchedule.rowid !== -1 && <Button color="secondary"
            disabled={newSchedule.attendings.length > 0}
            onClick={(event) => { props.deleteSchedule(newSchedule.rowid); }}
          >{'삭제'}</Button>}
        </div>

        <ButtonGroup color="primary" aria-label="outlined primary button group">
          {days.map(day => (
            <Button key={day.value}
              variant={day.value === newSchedule.day ? 'contained' : 'outlined'}
              onClick={(event) => { setNewSchedule({...newSchedule, day: day.value}); }}
            >{day.label}</Button>
          ))}
        </ButtonGroup>

        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <div className={classes.seconds}>
            <KeyboardTimePicker label="시작 시간"
              onChange={(date) => {
                setNewSchedule({...newSchedule, secondsFrom: seconds.fromDate(date)});
              }}
              value={seconds.toDate(newSchedule.secondsFrom)}
            />
            <KeyboardTimePicker label="종료 시간"
              onChange={(date) => {
                setNewSchedule({...newSchedule, secondsTo: seconds.fromDate(date)});
              }}
              value={seconds.toDate(newSchedule.secondsTo)}
            />
          </div>
        </MuiPickersUtilsProvider>

        <TextField label="제목"
          onChange={(event) => { setNewSchedule({...newSchedule, name: event.target.value}); }}
          value={newSchedule.name}
        />

        <TextField multiline rows={4} label="비고"
          onChange={(event) => { setNewSchedule({...newSchedule, note: event.target.value }); }}
          value={newSchedule.note}
        />

        {newSchedule.rowid > -1 && (
          <Typography variant="h6">{'참가자'}</Typography>
        )}

        {newSchedule.rowid > -1 && (
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={2} style={{whiteSpace: 'nowrap'}}>
                    <SelectingAttender context={props.context}
                      selectedAttender={selectedAttender}
                      setSelectedAttender={setSelectedAttender}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button color="primary"
                      disabled={selectedAttender.rowid < 0}
                      onClick={(event) => { insertAttender(selectedAttender.rowid); }}
                      style={{padding: 0}}
                    >{'추가'}</Button>
                  </TableCell>
                </TableRow>
                {newSchedule.attendings.map(attending => (
                  <TableRow key={attending.attenderRowid}>
                    <TableCell variant="head" className={classes.attender}>
                      <Avatar className={classes.avatar}
                        src={'data:image/jpeg;base64,' + attending.attenderPicture64}
                      />{attending.attenderName}
                    </TableCell>
                    <TableCell>
                      {date.used(attending.lastAttended)}
                    </TableCell>
                    <TableCell align="right">
                      <Button color={'secondary'}
                        onClick={(event) => { deleteAttender(attending.attenderRowid); }}
                        style={{padding: 0}}
                      >{'제외'}</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={(event) => {
          if (revision > 0) {
            props.setRevision((oldRevision) => (oldRevision + 1));
          }
          setNewSchedule(create.schedule());
        }}>{'취소'}</Button>
        <Button color="primary" onClick={(event) => {
          props.updateSchedule();
        }}>{'확인'}</Button>
      </DialogActions>
    </Dialog>
  );
}